import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, ArrowLeftRight, Download } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const YamlJsonConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [activeTab, setActiveTab] = useState<'yaml-to-json' | 'json-to-yaml'>('yaml-to-json');
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState('');
  const [prettyPrint, setPrettyPrint] = useState(true);
  const [yamlIndent, setYamlIndent] = useState(2);

  useSEO({
    title: 'YAML to JSON Converter - Convert YAML to JSON and JSON to YAML Online',
    description: 'Free online YAML to JSON converter. Convert YAML documents to JSON format and JSON data to YAML with customizable formatting options.',
    keywords: 'YAML to JSON, JSON to YAML, YAML converter, JSON converter, YAML parser',
    canonical: 'https://snap-tools.online/dev/yaml-json-converter'
  });

  // Simple YAML parser (basic implementation)
  const parseYaml = (yamlStr: string): any => {
    const lines = yamlStr.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
    const result: any = {};
    const stack: any[] = [result];
    let currentIndent = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      const indent = line.length - line.trimStart().length;

      if (trimmed.startsWith('---') || trimmed.startsWith('...')) {
        continue;
      }

      // Handle indentation changes
      while (stack.length > 1 && indent <= currentIndent) {
        stack.pop();
        currentIndent -= yamlIndent;
      }

      const current = stack[stack.length - 1];

      if (trimmed.startsWith('- ')) {
        // Array item
        const value = trimmed.substring(2).trim();
        if (!Array.isArray(current)) {
          throw new Error(`Invalid YAML: Expected array at line ${i + 1}`);
        }
        
        if (value.includes(':')) {
          // Object in array
          const obj: any = {};
          current.push(obj);
          stack.push(obj);
          currentIndent = indent;
          
          const [key, val] = value.split(':', 2);
          obj[key.trim()] = parseValue(val.trim());
        } else {
          current.push(parseValue(value));
        }
      } else if (trimmed.includes(':')) {
        // Key-value pair
        const colonIndex = trimmed.indexOf(':');
        const key = trimmed.substring(0, colonIndex).trim();
        const value = trimmed.substring(colonIndex + 1).trim();

        if (value === '') {
          // Check if next line is more indented (nested object or array)
          if (i + 1 < lines.length) {
            const nextLine = lines[i + 1];
            const nextIndent = nextLine.length - nextLine.trimStart().length;
            const nextTrimmed = nextLine.trim();
            
            if (nextIndent > indent) {
              if (nextTrimmed.startsWith('- ')) {
                // Array
                current[key] = [];
                stack.push(current[key]);
              } else {
                // Object
                current[key] = {};
                stack.push(current[key]);
              }
              currentIndent = indent;
            } else {
              current[key] = null;
            }
          } else {
            current[key] = null;
          }
        } else {
          current[key] = parseValue(value);
        }
      }
    }

    return result;
  };

  const parseValue = (value: string): any => {
    if (value === 'null' || value === '~') return null;
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    // Try to parse as number
    if (/^-?\d+\.?\d*$/.test(value)) {
      return value.includes('.') ? parseFloat(value) : parseInt(value, 10);
    }
    
    // Remove quotes if present
    if ((value.startsWith('"') && value.endsWith('"')) || 
        (value.startsWith("'") && value.endsWith("'"))) {
      return value.slice(1, -1);
    }
    
    return value;
  };

  // Simple JSON to YAML converter
  const jsonToYaml = (jsonStr: string): string => {
    const jsonObj = JSON.parse(jsonStr);

    const convertValue = (value: any, indent: number = 0): string => {
      const spaces = ' '.repeat(indent);
      
      if (value === null || value === undefined) {
        return 'null';
      }
      
      if (typeof value === 'boolean') {
        return value.toString();
      }
      
      if (typeof value === 'number') {
        return value.toString();
      }
      
      if (typeof value === 'string') {
        // Quote strings that contain special characters or start with special chars
        if (value.includes('\n') || value.includes(':') || value.includes('[') || 
            value.includes(']') || value.includes('{') || value.includes('}') ||
            value.match(/^[\d\-+]/) || value.includes('#') || value.trim() !== value) {
          return `"${value.replace(/"/g, '\\"')}"`;
        }
        return value;
      }
      
      if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        
        return value.map(item => {
          if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            const objYaml = Object.entries(item)
              .map(([k, v]) => `${spaces}  ${k}: ${convertValue(v, indent + yamlIndent)}`)
              .join('\n');
            return `${spaces}- \n${objYaml}`;
          } else {
            return `${spaces}- ${convertValue(item, indent + yamlIndent)}`;
          }
        }).join('\n');
      }
      
      if (typeof value === 'object') {
        if (Object.keys(value).length === 0) return '{}';
        
        return Object.entries(value)
          .map(([key, val]) => {
            if (typeof val === 'object' && val !== null) {
              const nestedYaml = convertValue(val, indent + yamlIndent);
              if (Array.isArray(val) || Object.keys(val).length > 0) {
                return `${spaces}${key}:\n${nestedYaml}`;
              } else {
                return `${spaces}${key}: ${nestedYaml}`;
              }
            } else {
              return `${spaces}${key}: ${convertValue(val, indent + yamlIndent)}`;
            }
          })
          .join('\n');
      }
      
      return String(value);
    };

    return convertValue(jsonObj);
  };

  const handleConversion = (value: string) => {
    setInput(value);
    setError('');
    setIsValid(true);

    if (!value.trim()) {
      setOutput('');
      return;
    }

    try {
      if (activeTab === 'yaml-to-json') {
        const result = parseYaml(value);
        setOutput(prettyPrint ? JSON.stringify(result, null, 2) : JSON.stringify(result));
      } else {
        const result = jsonToYaml(value);
        setOutput(result);
      }
    } catch (e) {
      setIsValid(false);
      setError(e instanceof Error ? e.message : 'Conversion failed');
      setOutput('');
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Output copied to clipboard');
    });
  };

  const downloadOutput = () => {
    const extension = activeTab === 'yaml-to-json' ? 'json' : 'yaml';
    const filename = `converted.${extension}`;
    
    const blob = new Blob([output], { 
      type: activeTab === 'yaml-to-json' ? 'application/json' : 'application/x-yaml' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const clearInputs = () => {
    setInput('');
    setOutput('');
    setError('');
    setIsValid(true);
    toast.success('All fields cleared');
  };

  const loadSampleData = () => {
    if (activeTab === 'yaml-to-json') {
      const sampleYAML = `# Sample YAML Configuration
name: "John Doe"
age: 30
active: true
address:
  street: "123 Main St"
  city: "Anytown"
  zipcode: 12345
hobbies:
  - reading
  - swimming
  - coding
contact:
  email: "john@example.com"
  phone: "+1-555-0123"
metadata:
  created: "2024-01-01"
  version: 1.0
  tags:
    - user
    - profile
    - sample`;
      setInput(sampleYAML);
      handleConversion(sampleYAML);
    } else {
      const sampleJSON = `{
  "name": "John Doe",
  "age": 30,
  "active": true,
  "address": {
    "street": "123 Main St",
    "city": "Anytown",
    "zipcode": 12345
  },
  "hobbies": [
    "reading",
    "swimming",
    "coding"
  ],
  "contact": {
    "email": "john@example.com",
    "phone": "+1-555-0123"
  },
  "metadata": {
    "created": "2024-01-01",
    "version": 1.0,
    "tags": [
      "user",
      "profile",
      "sample"
    ]
  }
}`;
      setInput(sampleJSON);
      handleConversion(sampleJSON);
    }
    toast.success('Sample data loaded');
  };

  const switchMode = () => {
    setActiveTab(activeTab === 'yaml-to-json' ? 'json-to-yaml' : 'yaml-to-json');
    setInput('');
    setOutput('');
    setError('');
    setIsValid(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            YAML ↔ JSON Converter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Convert YAML documents to JSON format and JSON data to YAML with customizable formatting 
            and advanced indentation options for perfect data interchange.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="bg-white rounded-lg p-1 shadow-lg">
            <button
              onClick={() => setActiveTab('yaml-to-json')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'yaml-to-json'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              YAML → JSON
            </button>
            <button
              onClick={() => setActiveTab('json-to-yaml')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'json-to-yaml'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              JSON → YAML
            </button>
          </div>
        </div>

        {/* Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8 mb-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">Conversion Options</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={prettyPrint}
                onChange={(e) => setPrettyPrint(e.target.checked)}
                className="rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
              />
              <span className="text-sm">Pretty Print JSON</span>
            </label>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                YAML Indentation
              </label>
              <select
                value={yamlIndent}
                onChange={(e) => setYamlIndent(Number(e.target.value))}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff]"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
                <option value={6}>6 spaces</option>
                <option value={8}>8 spaces</option>
              </select>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Input {activeTab === 'yaml-to-json' ? 'YAML' : 'JSON'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={loadSampleData}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sample
                  </button>
                  <button
                    onClick={switchMode}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                    title="Switch mode"
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>

              <textarea
                value={input}
                onChange={(e) => handleConversion(e.target.value)}
                placeholder={`Paste your ${activeTab === 'yaml-to-json' ? 'YAML' : 'JSON'} here...`}
                className={`w-full h-80 sm:h-96 p-3 sm:p-4 border-2 rounded-lg sm:rounded-xl resize-none font-mono text-xs sm:text-sm transition-colors ${
                  isValid 
                    ? 'border-gray-200 focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff]' 
                    : 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                }`}
              />

              {error && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 text-red-500 mr-2" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              <div className="mt-3 text-xs text-gray-600">
                <p>Input length: {input.length} characters</p>
                <p>Lines: {input.split('\n').length}</p>
                <p>Format: {activeTab === 'yaml-to-json' ? 'YAML Document' : 'JSON Object'}</p>
              </div>
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Output {activeTab === 'yaml-to-json' ? 'JSON' : 'YAML'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  {output && (
                    <>
                      <button
                        onClick={copyOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={downloadOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Download as file"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <textarea
                value={output}
                readOnly
                className="w-full h-80 sm:h-96 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm"
                placeholder={`Converted ${activeTab === 'yaml-to-json' ? 'JSON' : 'YAML'} will appear here...`}
              />

              {output && (
                <div className="mt-3 text-xs text-gray-600">
                  <p>Output length: {output.length} characters</p>
                  <p>Lines: {output.split('\n').length}</p>
                </div>
              )}

              {/* Conversion Info */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  {activeTab === 'yaml-to-json' ? 'YAML to JSON' : 'JSON to YAML'} Conversion
                </h4>
                <div className="text-xs text-blue-800 space-y-1">
                  {activeTab === 'yaml-to-json' ? (
                    <>
                      <p>• Comments (# lines) are ignored</p>
                      <p>• Indentation determines structure</p>
                      <p>• Arrays use dash (-) notation</p>
                      <p>• Strings are auto-detected and quoted if needed</p>
                    </>
                  ) : (
                    <>
                      <p>• Objects become indented key-value pairs</p>
                      <p>• Arrays become dash (-) notation</p>
                      <p>• Strings with special characters are quoted</p>
                      <p>• Boolean and numeric values preserved</p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default YamlJsonConverter;
